import { Component, memo } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { AccountIdsProps, withAccountIds } from '../../../auth/accessToken/withAccountIds';
import { isPartner } from '../../../auth/helpers';
import BackgroundAnimatedGrid from '../../../auth/login/components/BackgroundAnimatedGrid';
import { AppBarDinteroLogo } from '../../../components/AppBar';
import { Button, ButtonLink } from '../../../components/Buttons';
import Modal from '../../../components/Modal';
import chevronRightSvg from '../../../components/TopBar/icons/chevron-right.svg';
import { H2, P } from '../../../components/Typography';
import View from '../../../components/View';
import { parseISOStrict } from '../../../helpers/date';
import getConnections from '../../../helpers/getConnections';
import { distances, grid, palette } from '../../../styles/constants';
import { Account } from '../../../types/management-auth';

export interface WelcomeProps extends WithTranslation, AccountIdsProps {
    getAccount: (accountId: string) => void;
    closeModal: (accountId: string) => void;
    account?: Account;
    showWelcomeModal: boolean;
    exchangeTokenPartnerId: string | undefined;
}

const isNewMerchantAccount = (account?: Account) => {
    // So enterprise users not using payment connections
    // are not nagged after the first week.
    if (!account) {
        return false;
    }
    const createdAt = parseISOStrict(account.created_at || '');
    if (createdAt) {
        const now = new Date();
        const diff = now.valueOf() - createdAt.valueOf();
        const oneWeek = 7 * 24 * 60 * 60 * 1000;
        return diff <= oneWeek;
    }
    return false;
};

const merchantHasNoConnections = (account?: Account) => {
    if (!account) {
        return false;
    }
    const pending = getConnections(account, 'pending');
    const enabled = getConnections(account, 'enabled');
    return pending.length + enabled.length === 0;
};

class Welcome extends Component<WelcomeProps> {
    componentDidMount() {
        const accountId = this.props.prodAccountId || this.props.urlAccountId;
        this.props.getAccount(accountId);
    }

    render() {
        const { t, account, showWelcomeModal, closeModal, exchangeTokenPartnerId } = this.props;
        const accountId = this.props.prodAccountId || this.props.urlAccountId;
        const partner = isPartner(accountId);
        if (
            !partner && // hide modal for partners
            !exchangeTokenPartnerId && // hide modal for Dintero or partner administrators
            isNewMerchantAccount(account) &&
            merchantHasNoConnections(account) &&
            showWelcomeModal
        ) {
            return (
                <Modal fullscreen>
                    <SplitWrapper>
                        <ContentWrapper>
                            <TopWrapper>
                                <View maxWidth={grid.spans.span8}>
                                    <View justify="flex-start" flex={1}>
                                        <AppBarDinteroLogo color={palette.primary[500]} />
                                    </View>
                                    <View justify="flex-end" flex={1}>
                                        <div />
                                    </View>
                                </View>
                            </TopWrapper>
                            <Wrapper>
                                <Title>{t('welcome.modal.title')}</Title>
                                <P fontSize={16}>{t('welcome.modal.description_intro')}</P>
                                <br />
                                <br />
                                <Area>
                                    <P fontSize={16}>{t('welcome.modal.description_payments')}</P>
                                    <br />
                                    <Buttons>
                                        <ButtonLink
                                            className="stretch-768 has-icons large"
                                            onClick={() => {
                                                closeModal(accountId);
                                            }}
                                            to={`/${accountId}/settings/payment-connections`}
                                        >
                                            {t('welcome.modal.go_to_payment_connections')}
                                            <ChevronRightIcon />
                                        </ButtonLink>
                                    </Buttons>
                                    <br />
                                    <br />
                                    <P fontSize={16}>
                                        {t('welcome.modal.description_skip')}
                                    </P>
                                    <br />
                                    <Buttons>
                                        <Button
                                            className="outlined large"
                                            onClick={() => {
                                                closeModal(accountId);
                                            }}
                                        >
                                            {t('welcome.modal.skip')}
                                        </Button>
                                    </Buttons>
                                </Area>
                            </Wrapper>
                        </ContentWrapper>
                        <LineWrapper>
                            <BackgroundAnimatedGrid />
                            <CheckoutPreview>
                                <img src="/assets/images/Checkout.png" alt="" />
                            </CheckoutPreview>
                        </LineWrapper>
                    </SplitWrapper>
                </Modal>
            );
        }
        return null;
    }
}

const SplitWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    flex: 1;
    height: 100vh;
`;

const ContentWrapper = styled.div`
    flex: 1;
    padding-block: 105px;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding-inline: ${distances.normal};
`;

const LineWrapper = styled.div`
    flex: 1;
    background: ${palette.neutral[900]};
    overflow: hidden;
    position: relative;
    height: 100vh;
    display: flex;
    svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    @media (max-width: 700px) {
        display: none;
    }
`;

const CheckoutPreview = styled.div`
    position: relative;
    width: 100%;
    max-width: 400px;
    margin: auto;
    img {
        width: 100%;
        border-radius: 32px;
        box-shadow: 20px 40px 512px rgba(255, 255, 255, 0.3);
        padding: 4px;
    }

`;

const ChevronRightIcon = styled.div.attrs({ className: 'icon' })`
    mask-image: url(${chevronRightSvg});
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    background-color: white;
    width: 20px;
    height: 20px;
`;

const Title = styled(H2)`
font-size: 33.768px;
font-style: normal;
font-weight: 600;
line-height: 41.272px; /* 122.222% */
letter-spacing: -0.675px;

align-self: stretch;
margin-bottom: 7.5px;
`;

const Buttons = styled.div`
    display: flex;
`;

const TopWrapper = styled.div`
    position: relative;
    max-width: ${grid.spans.span8};
    margin-inline: auto;
    display: flex;
    width: 100%;
`;

const Wrapper = styled.div`
    position: relative;
    max-width: ${grid.spans.span8};
    margin-block: auto;
    margin-inline: auto;

    @media (max-width: 700px) {
        margin-block-start: ${distances.normal};
    }
`;
const Area = styled.div`
    margin-top: ${distances.large40};
    margin-bottom: ${distances.large40};
`;
const IconWrap = styled.div`
    text-align: center;
    margin-bottom: ${distances.normal};
`;

export default withAccountIds(withTranslation()(memo(Welcome)));
